<script setup lang="ts">
import type { KjdsListPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsListPageProps>(), {})

console.log('article: kjdsPageConfig', props.kjdsPageConfig)

const baseTitle = '展覧会'
let headTitle = baseTitle

const app = useNuxtApp()
const route = useRoute()
const storeArticleList = useStoreArticleList()
const storeIndex = useStoreIndex()

const pageData = {
  tags: [] as ArticleTag[],
  items: [] as Article[],
  meta: {} as ApiMeta,
}

const sortInfo = storeArticleList.getPageSortInfo(props.kjdsPageConfig)
const modelSort = ref(sortInfo?.defaultValue || 'pv_last_week')
if (route.query.sort) {
  modelSort.value = route.query.sort as string
}

const articleName = ref(props.kjdsPageConfig.articleTypeSlug)

const setHead = () => {
  let areaName = ''

  if (route.query.area) {
    areaName = app.$getQueyAreaName(route.query.area as string)
  }

  let headDescription = ''
  let items = []
  if (areaName) {
    headTitle = `${areaName}の${baseTitle}`
    headDescription = `「${areaName}の展覧会」の内容、会期、料金、休館日などの一覧です。ミュージアムの総合情報サイト、アイエム［インターネットミュージアム］による情報ページです。`
    items = [
      {
        text: baseTitle,
        disabled: false,
        to: { path: '/event' },
      },
      {
        text: headTitle,
        disabled: true,
      },
    ]
  } else {
    headDescription = 'ミュージアム情報サイト、アイエム［インターネットミュージアム］による展覧会・イベントの紹介。毎月200件以上の展覧会情報を発信、展覧会名・地域・ジャンルなどで検索できます。展覧会はアイエムで！'
    items = [
      {
        text: baseTitle,
        disabled: false,
        to: { path: '/event' },
      },
    ]
  }
  storeIndex.setBreadCrumbs(items)

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const onChangeSort = (val) => {
  modelSort.value = val
  const query = { ...route.query }
  if (val) {
    query.sort = val
  } else {
    delete query.sort
  }
  navigateTo({
    path: route.path,
    query,
  })
}

const initPage = async () => {
  const options: KjdsFetchListOptions = { query: { ...route.query } }
  if (route.query.term) {
    if (route.query.term === 'in') {
      // 開催中
      options.query = {
        ...options.query,
        dateBefore: useDayjs().format('YYYY/MM/DD 23:59:59'),
        dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00'),
      }
      // 初回のみ並び順を新着順にする
      if (!route.query.sort) {
        modelSort.value = 'open_date'
      }
    } else if (route.query.term === 'end') {
      // もうすぐ終了
      options.query = {
        ...options.query,
        dateBefore: useDayjs().format('YYYY/MM/DD 23:59:59'),
        dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00'),
      }
      // 初回のみ並び順を終了日順にする
      if (!route.query.sort) {
        modelSort.value = 'term_to'
      }
    } else if (route.query.term === 'start') {
      // もうすぐ開催
      options.query = {
        ...options.query,
        start: useDayjs().format('YYYY/MM/DD 23:59:59'),
      }
      // 初回のみ並び順を開始日順にする
      if (!route.query.sort) {
        modelSort.value = 'term_from'
      }
    } else {
      // 指定日の場合は日時範囲指定
      options.query = {
        ...options.query,
        dateBefore: useDayjs(route.query.term as string).format('YYYY/MM/DD 23:59:59'),
        dateAfter: useDayjs(route.query.term as string).format('YYYY/MM/DD 00:00:00'),
      }
    }
  } else {
    // 今日のイベントクエリー指定がない場合は今日以降のイベントを表示
    options.query = {
      ...options.query,
      dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00'),
    }
  }

  // エリアクエリーがある場合はエリアIDを展開
  if (route.query.area) {
    const areaQuery = route.query.area as string
    const ary = areaQuery.split('-')
    if (ary.length === 2) {
      const val = Number(ary[1])
      if (ary[0] === 'area') {
        const areaId = val - 0
        await storeIndex.fetchMaster()
        const masterArea = storeIndex.state.master.prefecture_area.details
        masterArea.some((area) => {
          if (area.id === areaId) {
            options.query['prefecture'] = area.value
            return true
          }
          return false
        })
      } else if ((ary[0] === 'pref')) {
        options.query['prefecture'] = ary[1]
      }
    }
  }

  const [_resSite, _resMaster, resList, resTags] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchList(props.kjdsPageConfig.list.mainListName, options),
    storeArticleList.fetchTags(articleName.value), // 公式タグ
  ])
  pageData.items = resList.data.articles as Article[]
  pageData.meta = resList.meta
  pageData.tags = resTags.data
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articlePage">
    <h2 class="c-heading_page">
      {{ headTitle }}
    </h2>

    <div
      class="c-link"
    >
      <nuxt-link
        class="c-link_btn c-link_btn_first"
        v-bind:to="{ path: '/jyunkai' }"
      >
        <div class="c-link_btn_text">
          巡回展
        </div>
      </nuxt-link>
      <nuxt-link
        class="c-link_btn"
        v-bind:to="{ path: '/schedule' }"
      >
        <div class="c-link_btn_text">
          {{ storeIndex.state.displayYear }}年の展覧会スケジュール
        </div>
      </nuxt-link>
    </div>

    <SearchPanel
      v-bind:article-name="articleName"
      v-bind:categories="[]"
      v-bind:tags="pageData.tags"
      v-bind:query="route.query"
    />

    <div class="p-articlePage_result">
      <div
        class="c-listHeader"
      >
        <div class="c-listHeader_col">
          <div class="c-listHeader_col_contentSearch">
            全 {{ pageData.meta.count.toLocaleString() }}件
          </div>
        </div>
        <div class="c-listHeader_dropDown">
          <v-select
            v-model="modelSort"
            class="IMCustomOverflowButton marginLeft c-searchSort"
            v-bind:items="sortInfo.items"
            hide-details
            variant="outlined"
            density="comfortable"
            v-on:update:model-value="onChangeSort"
          />
        </div>
      </div>

      <template
        v-if="!pageData.items?.length"
      >
        <div class="p-articlePage_empty">
          お探しの情報は見つかりませんでした
        </div>
      </template>

      <div class="p-articlePage_list">
        <EventItem
          v-for="item in pageData.items"
          v-bind:key="item.id"
          v-bind:item="item"
          v-bind:show-label="true"
        />
      </div>

      <template
        v-if="pageData.items?.length"
      >
        <Pagination
          v-bind:page="pageData.meta.page_no"
          v-bind:length="pageData.meta.total_page"
        />
      </template>

      <SearchList
        path="/event"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-link {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 24px;

  @media screen and (width <= 500px) {
    flex-wrap: wrap;
    justify-content: center;
  }

  &_btn {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 48%;
    height: 45px;
    margin: 0;
    transition: 300ms;
    border: 1px solid var(--g_color_table_border_gray);
    text-decoration: none;

    @media screen and (width <= 500px) {
      width: 100%;
    }

    &_first {
      @media screen and (width <= 500px) {
        margin-bottom: 12px;
      }
    }

    &:hover {
      opacity: 0.7;
    }

    &_text {
      color: var(--color_text_base);
      font-size: 15px;
      font-weight: bold;
      line-height: 2.286;
    }
  }
}
</style>
